import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SettingsModalContext = createContext(null);

export const useSettingsModal = () => {
  const value = useContext(SettingsModalContext);
  if (!value) {
    throw new Error('useSettingsModal must be used within SettingsModalProvider');
  }
  return value;
};

export const useOptionalSettingsModal = () => useContext(SettingsModalContext);

export const useOpenSettings = () => {
  const modal = useOptionalSettingsModal();
  const navigate = useNavigate();

  return useCallback(
    (tab = 'general', options = {}) => {
      if (modal) {
        modal.openSettings(tab, options);
        return;
      }

      const params = new URLSearchParams();
      if (tab && tab !== 'general') {
        params.set('tab', tab);
      }
      if (options.paymentResult != null) {
        params.set('success', String(options.paymentResult));
      }
      const query = params.toString();
      navigate(query ? `/settings?${query}` : '/settings');
    },
    [modal, navigate]
  );
};

export const SettingsModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('general');
  const [paymentAlert, setPaymentAlert] = useState(null);

  const openSettings = useCallback((nextTab = 'general', options = {}) => {
    const allowed = ['general', 'personalization', 'profile', 'packages', 'wallet'];
    setTab(allowed.includes(nextTab) ? nextTab : 'general');
    if (options.paymentResult === '1' || options.paymentResult === 1) {
      setPaymentAlert('success');
    } else if (options.paymentResult === '0' || options.paymentResult === 0) {
      setPaymentAlert('error');
    }
    setOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({ open, tab, setTab, openSettings, closeSettings, paymentAlert, setPaymentAlert }),
    [open, tab, openSettings, closeSettings, paymentAlert]
  );

  return <SettingsModalContext.Provider value={value}>{children}</SettingsModalContext.Provider>;
};
